import { Check, Close } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useState } from "react";
import {
  IconButton,
  NoteComponent,
  EConfirmModalType,
} from "src/shared/components";
import { PartnerLabel } from "src/shared/components/PartnerLabel";
import { currenciesSymbols } from "src/shared/constants/currency.constants";
import { useConfirmModal } from "src/shared/hooks";
import { EIconNames } from "src/shared/types/iconNames";
import { useUpdateFinalReportNoteMutation } from "../api/api";
import { formatRuDate } from "../helpers/formatRuDate";
import { IFinalReportItem } from "../model/types";

export const useGetTableColumns = () => {
  const [noteModalData, setNoteModalData] = useState<{
    summary_reward_id: number;
    note: string;
  } | null>(null);

  const [deleteNoteModalData, setDeleteNoteModalData] = useState<{
    summary_reward_id: number;
    note: string;
  } | null>(null);

  const [updateBonusNoteRequest, { isLoading: isLoadingUpdateNote }] =
    useUpdateFinalReportNoteMutation();

  async function updateBonusNote(data: {
    note: string;
    summary_reward_id: number;
  }) {
    try {
      await updateBonusNoteRequest(data).unwrap();
      closeModal();
      setDeleteNoteModalData(null);
      setNoteModalData(null);
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }

  const {
    modal: deleteNoteConfirmModal,
    showModal,
    closeModal,
  } = useConfirmModal({
    onResolve: () => {
      if (deleteNoteModalData) {
        updateBonusNote(deleteNoteModalData);
      }
    },
    isLoading: isLoadingUpdateNote,
    confirmModalType: EConfirmModalType.WARNING,
    children: <Typography>Удалить заметку?</Typography>,
  });

  const noteModal = (
    <NoteComponent
      key={noteModalData?.summary_reward_id}
      open={Boolean(noteModalData)}
      onClose={() => setNoteModalData(null)}
      value={noteModalData?.note || ""}
      handleSave={(v) => {
        if (noteModalData) {
          updateBonusNote({ ...noteModalData, note: v });
        }
      }}
      isLoading={isLoadingUpdateNote}
    />
  );

  const columns: GridColDef<IFinalReportItem>[] = [
    {
      headerName: "Партнер",
      field: "partner",
      width: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <PartnerLabel
          name={row.partner.name}
          organizationForm={row.partner.organization_form?.value}
        />
      ),
    },
    {
      headerName: "КДА Партнера",
      field: "partner_contract_number",
      width: 150,
    },
    {
      headerName: "Система налогообложения",
      field: "tax_system",
      width: 150,
      renderCell({ row }) {
        return row.tax_system.name === "С НДС" ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
    {
      headerName: "Сумма бонуса по открутке в валюте КДА",
      field: "bonus_sum_by_spend",
      width: 150,
      valueFormatter(v, row) {
        if (v == null) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency.name]}`;
      },
    },
    {
      headerName: "Накопление предыдущих периодов",
      field: "previous_period_accumulation",
      width: 150,
    },
    {
      headerName: "Акт",
      field: "humanized_state",
      width: 150,
    },
    {
      headerName: "Сумма акта",
      field: "bonus_to_pay",
      width: 150,
      valueFormatter(v, row) {
        if (v == null) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency.name]}`;
      },
    },
    {
      headerName: "Дата выплаты",
      field: "paid_date",
      width: 160,
      renderCell({ row }) {
        if (!row.paid_date) return null;

        return formatRuDate(row.paid_date);
      },
    },
    {
      headerName: "Выплаченная сумма",
      field: "paid_amount",
      width: 200,
      valueFormatter(v, row) {
        if (v == null) return "";

        return `${v} ${currenciesSymbols[row.complex_contract_currency.name]}`;
      },
    },
    {
      headerName: "Примечание",
      field: "note",
      width: 200,
      renderCell: ({ row }) => {
        const { note, summary_reward_id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {note ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  paddingLeft: "30px",
                  ["&:hover button:nth-of-type(2)"]: { visibility: "visible" },
                }}
              >
                <IconButton
                  iconName={EIconNames.NOTE}
                  sx={{ color: "primary.main" }}
                  onClick={() => setNoteModalData({ summary_reward_id, note })}
                />

                <IconButton
                  iconName={EIconNames.CROSS_BUTTON}
                  sx={{ visibility: "hidden" }}
                  onClick={() => {
                    setDeleteNoteModalData({
                      summary_reward_id,
                      note: "",
                    });
                    showModal();
                  }}
                />
              </Box>
            ) : (
              <IconButton
                iconName={EIconNames.PLUS_BUTTON}
                onClick={() =>
                  setNoteModalData({ summary_reward_id, note: "" })
                }
              />
            )}
          </Box>
        );
      },
    },

    {
      headerName: "Наличие справки об НДС",
      field: "has_tax_certificate",
      width: 250,
      renderCell({ row }) {
        if (row.has_tax_certificate === null) return null;

        return row.has_tax_certificate ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
    {
      headerName: "Оригинал договора",
      field: "has_contract_original",
      width: 100,
      renderCell({ row }) {
        return row.has_contract_original ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
    {
      headerName: "ДС",
      field: "extra_agreement_has_original",
      width: 100,
      renderCell({ row }) {
        if (row.extra_agreement_has_original === null) return null;

        return row.extra_agreement_has_original ? (
          <Check color="success" />
        ) : (
          <Close color="error" />
        );
      },
    },
  ];

  return {
    columns,
    noteModal,
    deleteNoteConfirmModal,
  };
};
